import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import * as _ from 'lodash';
import { isEqual } from 'lodash';
import { AppSelections, AppTable, AppTableAction, AppTableColumn, AuthPermissions, CustomComponentConfig } from '../../_models/models';
import { getSmartSortDirection } from './app-table.utils';

import { animate, state, style, transition, trigger, } from '@angular/animations';
import { Common } from '../../_common/common.helper';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomComponentHostDirective } from '../../_directives/custom-component-host.directive';
import {
  EXCHANGE_DEALS_EXTERNAL_ALERTS_SENTINEL_GROUP,
  EXCHANGE_DEALS_INTERNAL_ALERTS_SENTINEL_GROUP,
  EXTERNAL_ALERTS_SENTINEL_GROUP,
  INTERNAL_ALERTS_SENTINEL_GROUP
} from '../../_services/alerts/types/config.types';

@Component({
  selector: 'app-table',
  animations: [
    trigger('hideColumn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(300px)', offset: 1.0 }),
        animate('0.5s ease-in')
      ]),
      transition(':leave', [
        style({ opacity: .5, transform: 'translateY(-75%)',  offset: 0.3 }),
        animate('0.5s ease-out')
      ]),
    ]),
    trigger('freezeColumn', [
      state('freeze', style({
        color: '#FDD032'
      })),
      state('unfreeze', style({
        color: 'inherit'
      })),
    ]),
  ],
  templateUrl: './app-table.component.html',
  styleUrls: ['./app-table.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppTableComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('appTable') appTableEl;
  @ViewChildren(CustomComponentHostDirective) customComponentHosts!: CustomComponentHostDirective[];

  @Input() tableTitle: string;
  @Input() tableType: string;
  @Input() innerTableColumns: Array<any>;
  @Input() table: AppTable;
  @Input() selections: AppSelections;
  @Input() authPermissions: AuthPermissions;
  @Input() scrollConfig: any = { x: 'inherit' };
  @Input() isExternal = false;
  @Input() idProp: string;
  @Input() hasAccess: string;
  @Input() exportCSV = false;
  @Input() hasFooter = false;
  @Input() tableTotalsData: any;
  @Input() totalRows: number;
  @Input() isTableLoading: boolean;
  @Input() isDownloading: boolean;
  @Input() isPaginated = true;
  @Input() hasSizeChanger = true;

  @Output() trafficSourcesAction: EventEmitter<any> = new EventEmitter();
  @Output() adServerTechAction: EventEmitter<any> = new EventEmitter();
  @Output() termsOfServiceAction: EventEmitter<any> = new EventEmitter();
  @Output() emailTemplateAction: EventEmitter<any> = new EventEmitter();
  @Output() inventoryPackagesAction: EventEmitter<any> = new EventEmitter();
  @Output() kvpValuesAction: EventEmitter<any> = new EventEmitter();
  @Output() inventoryPackagesGeoAction: EventEmitter<any> = new EventEmitter();
  @Output() dealsAction: EventEmitter<any> = new EventEmitter();
  @Output() exchangeDealsAction: EventEmitter<any> = new EventEmitter();
  @Output() exchangeForecastsAction: EventEmitter<any> = new EventEmitter();
  @Output() exchangePartnersAction: EventEmitter<any> = new EventEmitter();
  @Output() universalMediaListsAction: EventEmitter<any> = new EventEmitter();
  @Output() universalMediaListsDomainAction: EventEmitter<any> = new EventEmitter();
  @Output() universalMediaListAssociatedDealsAction: EventEmitter<any> = new EventEmitter();
  @Output() publishersAction: EventEmitter<any> = new EventEmitter();
  @Output() placementsAction: EventEmitter<any> = new EventEmitter();
  @Output() domainBundlesAction: EventEmitter<any> = new EventEmitter();
  @Output() domainBundlesChangesAction: EventEmitter<any> = new EventEmitter();
  @Output() badvAction: EventEmitter<{ row: any; id: number | string; action: string }> = new EventEmitter();
  @Output() rolesAction: EventEmitter<any> = new EventEmitter();
  @Output() usersAction: EventEmitter<any> = new EventEmitter();
  @Output() billingHistoryAction: EventEmitter<any> = new EventEmitter();
  @Output() alertRowAction: EventEmitter<{ row: any; action: string }> = new EventEmitter();
  @Output() scheduledReportsAction: EventEmitter<any> = new EventEmitter();

  @Output() tablePageChanged: EventEmitter<any> = new EventEmitter();
  @Output() tableSortChanged: EventEmitter<any> = new EventEmitter();
  @Output() selectionsChanged: EventEmitter<any> = new EventEmitter();
  @Output() pageIndexChanged: EventEmitter<any> = new EventEmitter();
  @Output() exportClicked: EventEmitter<any> = new EventEmitter();

  @Output() rowAction: EventEmitter<any> = new EventEmitter();
  sortDirection: 'asc' | 'desc' | null | undefined;

  confirmId: number | string;
  columnsProps: string[];
  totalClientSideRows: number;
  frozenColumnsLeftValues = [];

  private _tableData: any;
  private _tableColumns: AppTableColumn[];

  @Input()
  set tableData(tableData: any[]) {
    if (!isEqual(this._tableData, tableData)) {
      this._tableData = tableData;
      if (this._tableData) {
        this.totalClientSideRows = this._tableData.length;
        setTimeout(() => {
          this.calcStickyWidthLeft();
          this._tableData?.forEach((row, idx) => {
            if (row.isExpanded && row.expandRowComponent) {
              this.loadCustomComponent(row, idx, 'expandRowComponent');
            }
            if (row.trComponent) {
              this.loadCustomComponent(row, idx, 'trComponent');
            }
          });
        }, 100);
      }
    }
  }
  get tableData(): any[] {
    return this._tableData;
  }

  @Input()
  set tableColumns(tableColumns: AppTableColumn[]) {
    this._tableColumns = tableColumns;
    if (this._tableColumns) {
      setTimeout(() => {
        this.calcStickyWidthLeft();
      }, 100);
    }
  }
  get tableColumns(): AppTableColumn[] {
    return this._tableColumns;
  }

  @HostListener('document:click', ['$event'])
  onClick(event: Event) {
    this.confirmId = null;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    public common: Common,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    if (!this.tableData) {
      return;
    }
    this.columnsProps = _.map(this.tableColumns, 'prop');
  }

  ngAfterViewInit() {
    if (!this.tableData || this.tableData.length === 0) {
      return;
    }
    setTimeout(() => this.calcStickyWidthLeft(), 0);
  }

  hasPermissions() {
    if (this.authPermissions && Object.prototype.hasOwnProperty.call(this.authPermissions, 'showActions')) {
      return this.authPermissions.showActions && Object.values(this.authPermissions).indexOf(true) > -1;
    } else {
      return this.authPermissions && Object.values(this.authPermissions).indexOf(true) > -1;
    }
  }

  onPageIndexChanged(event) {
    this.table.pageIndex = event;
    this.tablePageChanged.emit({ ...this.table });
  }

  onPageSizeChanged(event) {
    this.table.pageSize = event;
    this.table.pageIndex = 1;
    this.tablePageChanged.emit({ ...this.table });
  }

  onTableSortChange(sortEvent: { key: string; value: 'ascend' | 'descend' | null }): void {
    if (!this.table) return;

    if (this.tableType === 'clientSide') {
      this.handleClientSideSort(sortEvent);
    } else {
      this.handleServerSideSort(sortEvent);
    }
  }

  private handleClientSideSort(sortEvent: { key: string; value: 'ascend' | 'descend' | null }): void {
    const { key, value } = sortEvent;
    if (value) {
      this.sortDirection = value === 'ascend' ? 'asc' : 'desc';
      if (this._tableData) {
        this._tableData = _.orderBy(this._tableData, [val => {
          const sortVal = _.get(val, key);
          return (typeof sortVal === 'string' || sortVal instanceof String) ? sortVal.toLowerCase() : sortVal;
        }], [this.sortDirection]);
      }
    } else {
      this.sortDirection = null;
    }
    this.cdr.markForCheck();
  }

  private handleServerSideSort(sortEvent: { key: string; value: 'ascend' | 'descend' | null }): void {
    const tableStateToEmit = { ...this.table };
    let nextSortDirection: 'ASC' | 'DESC' | null = null;
    const currentSortKey = this.table.sortBy;
    const currentSortDirection = this.table.sortDirection;

    if (currentSortKey === sortEvent.key) {
      if (currentSortDirection === 'ASC') {
        nextSortDirection = 'DESC';
      } else {
        nextSortDirection = 'ASC';
      }
    } else {
      const clickedColumn = this.tableColumns.find(col =>
        col.prop === sortEvent.key || col.sortingProp === sortEvent.key
      );
      if (clickedColumn) {
        nextSortDirection = getSmartSortDirection(clickedColumn);
      } else {
        nextSortDirection = this.table.initialSortDirection || 'ASC';
      }
    }

    tableStateToEmit.sortBy = sortEvent.key;
    tableStateToEmit.sortDirection = nextSortDirection;

    this.tableSortChanged.emit(tableStateToEmit);
    this.cdr.markForCheck();
  }

  onItemsSelectionsChange(changedRow = null, changeAction = null) {
    this.selectionsChanged.emit({ selections: this.selections, changedRow: changedRow, changeAction: changeAction });
  }

  selectAll() {
    this.selections.excludedItems = [];
    this.selections.selectedItems = [];

    if (this.tableType === 'clientSide') {
      const isNotSelectAll = !this.selections.isSelectAll;
      const areExcludedItemsNotEmpty = this.selections.excludedItems.length > 0;

      this.selections.isSelectAll = isNotSelectAll || areExcludedItemsNotEmpty;
    }

    this.onItemsSelectionsChange();
  }


  isRowSelected(row, idProp: string) {
    if (this.selections.isSelectAll) {
      return !this.isRowExcluded(row, idProp);
    }
    return _.findIndex(this.selections.selectedItems, { [idProp]: row[idProp] }) > -1;
  }

  isRowExcluded(row, idProp: string) {
    return _.findIndex(this.selections.excludedItems, { [idProp]: row[idProp] }) > -1;
  }

  updateExcludedItems(row, idProp: string) {
    if (this.isRowExcluded(row, idProp)) {
      _.remove(this.selections.excludedItems, { [idProp]: row[idProp] });
    } else {
      this.selections.excludedItems.push(row);
    }
  }

  updateSelectedObjects(row, idProp: string) {
    if (this.selections.isSelectAll) {
      this.updateExcludedItems(row, idProp);
    } else {
      const isSelected = this.isRowSelected(row, idProp);
      if (isSelected) {
        _.remove(this.selections.selectedItems, { [idProp]: row[idProp] });
      } else {
        this.selections.selectedItems.push(row);
      }
    }

    if (this.tableType === 'clientSide') {
    // check if user unselected one by one all rows in all pages
      if (this.selections.selectedItems.length === 0 && this.selections.excludedItems.length === this.totalRows) {
        this.selections.isSelectAll = false;
        this.selections.excludedItems = [];
      }
      // check if user selected one by one all rows in all pages
      if (this.selections.selectedItems.length === this.totalRows) {
        this.selections.isSelectAll = true;
        this.selections.selectedItems = [];
      }
    }
    this.onItemsSelectionsChange(row);
  }

  onShowConfirmDialog(rowId: number | string, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.confirmId = rowId;
    this.cdr.markForCheck();
  }

  onRowAction(row: any, id: number | string, rowIndex: number, actionId: string, data: any = null) {
    const updateRow = _.cloneDeep(row);
    switch (actionId) {
      case 'trafficSourcesView':
      case 'trafficSourcesEdit':
      case 'trafficSourcesDelete':
        this.trafficSourcesAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'adServerTechEdit':
      case 'adServerTechDelete':
        this.adServerTechAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'termsOfServiceView':
        this.termsOfServiceAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'emailTemplateView':
      case 'emailTemplateEdit':
        this.emailTemplateAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'inventoryPackagesEdit':
      case 'inventoryPackagesDelete':
      case 'inventoryPackageNameId':
      case 'inventoryPackageSelectForDeal':
      case 'inventoryPackageCopyName':
      case 'inventoryPackagesRunForecast':
        this.inventoryPackagesAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'kvpValueDelete':
        this.kvpValuesAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'dealEdit':
      case 'dealDuplicate':
      case 'dealCopyId':
      case 'dealStatusToggle':
        this.dealsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'exchangeDealEdit':
      case 'exchangeDealDuplicate':
        this.exchangeDealsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'exchangeForecastsActionDelete':
        this.exchangeForecastsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'exchangePartnersActionDelete':
      case 'exchangePartnersActionUpdateStatus':
        this.exchangePartnersAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'universalMediaListsActionEdit':
      case 'universalMediaListsActionDuplicate':
      case 'universalMediaListsActionArchive':
      case 'universalMediaListsActionUnarchive':
        this.universalMediaListsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'universalMediaListsDomainActionDelete':
        this.universalMediaListsDomainAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'universalMediaListAssociatedDealsActionRemove':
        this.universalMediaListAssociatedDealsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'publisherStatusToggle':
      case 'publisherEdit':
      case 'publisherDelete':
      case 'publisherView':
        this.publishersAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'placementStatusToggle':
      case 'placementEdit':
      case 'placementDuplicate':
      case 'placementView':
        this.placementsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'domainBundleDelete':
      case 'domainBundlesIncludeSubDomains':
        this.domainBundlesAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'cancelAction':
        this.domainBundlesChangesAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'badvDelete':
        this.badvAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'usersView':
      case 'usersEdit':
      case 'usersDelete':
        this.usersAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'roleEdit':
      case 'roleDelete':
        this.rolesAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'billPaymentDetails':
        this.billingHistoryAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      case 'alertToggleAction':
      case INTERNAL_ALERTS_SENTINEL_GROUP:
      case EXTERNAL_ALERTS_SENTINEL_GROUP:
      case EXCHANGE_DEALS_INTERNAL_ALERTS_SENTINEL_GROUP:
      case EXCHANGE_DEALS_EXTERNAL_ALERTS_SENTINEL_GROUP:
      case 'alertDeleteAction':
        this.alertRowAction.emit({ row: updateRow, action: actionId });
        break;
      case 'scheduledReportEdit':
      case 'scheduledReportDelete':
      case 'scheduledReportToggleStatus':
        this.scheduledReportsAction.emit({ row: updateRow, id: id, action: actionId });
        break;
      default:
    }

    this.rowAction.emit({ row: updateRow, id: id, action: actionId, data: data });
  }

  onExportClicked($event) {
    this.exportClicked.emit($event);
  }

  lodashGet(obj, path, columnId = '') {
    let val = _.get(obj, path);
    if (columnId === 'dealsDefaultBidfloor' && val) {
      val = '$' + val;
    }
    return val;
  }

  calcStickyWidthLeft() {
    this.frozenColumnsLeftValues = [];
    let left = 0;
    this.tableColumns.forEach((col, idx) => {
      if (col.isFrozenCol) {
        this.frozenColumnsLeftValues[idx] = left;
        const width = _.get(this.appTableEl, `listOfNzThComponent._results[${idx}].cdr._view.parent.nodes[0].renderElement.clientWidth`);
        left += width;
      }
    });
    if (this.cdr && !this.cdr['destroyed']) {
      this.cdr.detectChanges();
    }
  }

  getStickyWidthLeft(idx: number) {
    if (this.frozenColumnsLeftValues[idx] >= 0) {
      return `${this.frozenColumnsLeftValues[idx]}px`;
    }
  }

  getHrefLink(link: string, id: number | string) {
    return link.replace('{{id}}', id.toString());
  }

  getDisplayNameFormatterValue(col) {
    return <AppTableColumn>col.displayNameFormatter(col);
  }

  routeIf(route: string[], openTab: boolean = false): void {
    if (openTab) {
      const urlTree = this.router.createUrlTree(route, { relativeTo: this.route });
      const url = this.router.serializeUrl(urlTree);
      window.open(url, '_blank');
    } else {
      this.router.navigate(route, { relativeTo: this.route });
    }
  }

  loadCustomComponent(row, rowIndex, customComponentRowKey: string): void {
    const componentConfig: CustomComponentConfig = row[customComponentRowKey];
    const host =
      this.customComponentHosts.filter((node, idx) => idx === rowIndex)[0];
    if (componentConfig?.component) {
      host.componentConfig = componentConfig;
      if (componentConfig.data) {
        host.componentConfig.data = componentConfig.data;
      }
    }
  }

  getVisibleActions(row: any, actions: AppTableAction[]): AppTableAction[] {
    if (!actions) {
      return [];
    }

    return actions.filter(action => !action.condition || action.condition(row));
  }

  ngOnDestroy(): void {
    this.cdr.detach();
  }

  getMoreTagCounter(items: any[]): number {
    if (!items) return 0;
    return items.length - 1;
  }

  getListItemDisplayText(item: any, column: AppTableColumn): string {

    const displayTextStrategies = new Map([
      ['formatterOnly', (specItem: any, specColumn: AppTableColumn) =>
        this.processFormatterResult(specColumn.moreTag.listItemFormatter(specItem), specItem)],
      ['displayNameOnly', (specItem: any, specColumn: AppTableColumn) =>
        this.lodashGet(specItem, specColumn.moreTag.displayNameProp)],
      ['formatterAndDisplayName', (specItem: any, specColumn: AppTableColumn) => {
        const formatterResult = this.processFormatterResult(specColumn.moreTag.listItemFormatter(specItem), specItem);
        const displayNameResult = this.lodashGet(specItem, specColumn.moreTag.displayNameProp);
        return formatterResult && displayNameResult ? `${formatterResult} - ${displayNameResult}` : formatterResult || displayNameResult;
      }],
      ['default', (specItem: any, specColumn: AppTableColumn) =>
        this.lodashGet(specItem, specColumn.moreTag?.displayNameProp || '')]
    ]);

    const hasFormatter = !!column.moreTag?.listItemFormatter;
    const hasDisplayName = !!column.moreTag?.displayNameProp;

    let strategyKey = 'default';
    if (hasFormatter && !hasDisplayName) strategyKey = 'formatterOnly';
    else if (!hasFormatter && hasDisplayName) strategyKey = 'displayNameOnly';
    else if (hasFormatter && hasDisplayName) strategyKey = 'formatterAndDisplayName';

    const strategy = displayTextStrategies.get(strategyKey);
    return strategy ? strategy(item, column) : '';
  }

  private processFormatterResult(formatterResult: any, item: any): string {
    if (typeof formatterResult === 'object' && formatterResult.text) {
      return this.processBoldVariables(formatterResult, item);
    }
    if (typeof formatterResult === 'string') {
      return formatterResult;
    }
    return '';
  }

  processBoldVariables(formatterResult: { text: string; boldVariables: string[] }, item: any): string {
    let result = formatterResult.text;

    formatterResult.boldVariables.forEach(variableName => {
      const variableValue = this.lodashGet(item, variableName);
      if (variableValue) {
        result = result.replace(variableValue, `<strong>${variableValue}</strong>`);
      }
    });

    return result;
  }

  shouldUseInnerHtml(column: AppTableColumn, displayText?: string): boolean {
    return displayText?.includes('<strong>') ?? false;
  }

  getMainCellDisplayText(item: any, column: AppTableColumn): string {
    if (column.moreTag?.displayNameProp) {
      return this.lodashGet(item, column.moreTag.displayNameProp, column.id.toString());
    }

    if (column.moreTag?.listItemFormatter) {
      const formatterResult = column.moreTag.listItemFormatter(item);
      if (typeof formatterResult === 'object' && formatterResult.text) {
        return this.processBoldVariables(formatterResult, item);
      }
      return formatterResult as string;
    }

    return '';
  }

  getPopoverListStyle(moreTag: any): any {
    if (!moreTag?.maxVisibleItems) {
      return {};
    }

    const maxHeight = (moreTag.maxVisibleItems * 24) + 16;
    return {
      'max-height': `${maxHeight}px`,
      'overflow-y': 'auto',
      'overflow-x': 'hidden',
    };
  }
}
